import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export const initSentry = () => {
  const environment = process.env.REACT_APP_ENV || 'local-dev';
  const isLocalDevelopment = environment === 'local-dev';
  console.log('Sentry inited');
  Sentry.init({
    // Only set DSN if NOT in local-dev (this prevents sending errors remotely)
    dsn: isLocalDevelopment ? undefined : process.env.REACT_APP_SENTRY_DSN,

    // Release & Environment
    release: process.env.REACT_APP_VERSION || '1.0.0',
    environment,
    enabled: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Capture console logs in replays
        maskAllInputs: true,
      }),
    ],

    // Performance Monitoring - environment-specific sampling
    tracesSampleRate:0.5,

    // Note: tracePropagationTargets removed to avoid CORS issues with API Gateway
    // This only affects distributed tracing, not error tracking

    // Session Replay sampling - environment-specific
    replaysSessionSampleRate: 0.3,
    replaysOnErrorSampleRate: 1.0, // Always capture sessions with errors

    // Ignore noisy browser extension errors
    ignoreErrors: [
      // Browser extensions
      'Extension context invalidated',
      'chrome-extension://',
      'moz-extension://',
      // Ad blockers
      'adsbygoogle',
      'BlockAdBlock',   
      // 'Network request failed',
      // 'NetworkError',    
      // 'Load failed',
      // Asset loading errors (images, fonts, chunks)
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',
      /loading.*\.(png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot)/i,
      // React DevTools
      '__REACT_DEVTOOLS',
      // Google Maps errors and warnings
      'Google Maps',
      'google is not defined',
      'gmp-internal',
      'gmp-place',
      'Element with name "gmp-',
      /google\.maps/i,
      'LoadScript',
      // ApexCharts date errors (we handle these in our code)
      'You have provided invalid Date format',
      /apexcharts.*invalid.*date/i,
      // Other noise
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],

    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Facebook tracking
      /graph\.facebook\.com/i,
      // Google services
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /maps\.googleapis\.com/i,
      /maps\.gstatic\.com/i,
      // Third-party libraries
      /apexcharts/i,
    ],

    beforeSend(event, hint) {
      // Filter PII from error messages
      if (event.message) {
        // Remove emails
        event.message = event.message.replace(
          /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
          '[EMAIL]'
        );
        // Remove phone numbers
        event.message = event.message.replace(
          /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
          '[PHONE]'
        );
      }

      // Filter PII from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.message) {
            breadcrumb.message = breadcrumb.message.replace(
              /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
              '[EMAIL]'
            );
          }
          return breadcrumb;
        });
      }

      // Filter out errors from browser extensions (double check)
      const errorMessage = event.exception?.values?.[0]?.value || '';
      if (
        errorMessage.includes('Extension context invalidated') ||
        errorMessage.includes('chrome-extension://') ||
        errorMessage.includes('moz-extension://')
      ) {
        return null;
      }

      // Filter out image/asset loading errors
      const isResourceError = event.exception?.values?.some((exception) => {
        const value = exception.value || '';
        const type = exception.type || '';

        // Check if it's a resource loading error
        return (
          type === 'Error' &&
          (
            value.includes('ChunkLoadError') ||
            value.includes('Loading chunk') ||
            value.match(/\.(png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot)/) ||
            value.includes('Failed to fetch')
          )
        );
      });

      if (isResourceError) {
        return null;
      }

      // Filter breadcrumbs to remove noise
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.filter((breadcrumb) => {
          const message = breadcrumb.message || '';
          const url = breadcrumb.data?.url || '';

          // Remove Google Maps console warnings
          if (breadcrumb.category === 'console' && breadcrumb.level === 'warning') {
            if (
              message.includes('gmp-') ||
              message.includes('Google Maps') ||
              message.includes('google.maps') ||
              message.includes('LoadScript') ||
              message.includes('PlaceAutocompleteElement')
            ) {
              return false;
            }
          }

          // Remove Google Maps API calls
          if (breadcrumb.type === 'http' || breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
            if (
              url.includes('maps.googleapis.com') ||
              url.includes('maps.gstatic.com') ||
              url.includes('google-analytics.com')
            ) {
              return false;
            }
          }

          // Remove successful asset loads
          if (breadcrumb.type === 'http') {
            const isAsset = /\.(png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot)$/i.test(url);
            const isSuccessful = breadcrumb.data?.status_code === 200;
            if (isAsset && isSuccessful) {
              return false;
            }
          }

          return true;
        });
      }

      // In local-dev: log to console but don't send to Sentry
      if (isLocalDevelopment) {
        console.group('[Sentry] Error captured (local-dev only - not sent)');
        console.error('Error:', event.exception?.values?.[0]?.value || event.message);
        console.log('Full event:', event);
        console.groupEnd();
        return null; // Don't send to remote
      }

      // In production/staging: send the event
      return event;
    },
  });
};
